import { useT } from "../../lib/i18n";
import { ControlLabel } from "../ui/Field";
import { useEffect, useRef, useState, type DragEvent, type ReactNode } from "react";
import { ApiTag } from "../apilens/ApiLens";

/** 窗口化时上下各多挂几行，滚动时不露白 */
const OVERSCAN = 15;
/** 行高兜底（h-row 是 40px，两行内容的格子会撑到 47 左右）；真值从已挂的行量 */
const ROW_H_FALLBACK = 47;
/** 少于这么多行不窗口化：小表整张挂上最省事，也不受滚动位置影响 */
const VIRTUAL_MIN_ROWS = 150;

export interface Column<T> {
  /** 按行给这一格加类：把「这一格有问题」标在格子上，而不是整行染色 */
  cellClass?: (row: T) => string | undefined;
  key: string;
  title: ReactNode;
  /** 接口视角的登记 id（src/api-lens）：这一列是哪个配置项。开关打开时表头下挂字段名，选取时点这一列任意一格都认得出 */
  api?: string;
  /** 表头下的小字，通常写「自动计算」这类口径说明 */
  sub?: ReactNode;
  width?: number | string;
  /**
   * 对齐服务于扫读，不服务于好看。**按「这一列是什么」定，不按它长什么样定。**
   *
   * - `left`（缺省）—— 文本：名称、编码、说明。左边给出稳定的起始边，眼睛竖着扫一列。
   * - `right` —— **要互相比较的量**：金额、张数、权重、价格、百分比。个位对齐才比得出大小，
   *   `ALIGN.right` 已经连 `tabular-nums` 一起给了，不用各写一遍。
   * - `center` —— **定宽单体**：缩略图、单个 Tag、按钮组、图标。它们没有文字基线可对。
   *
   * **行标识数字（关卡号 / 序号 / 排序号）算文本，靠左，不算量。** 没人会问「第 12 关比
   * 第 3 关大多少」，它回答的是「这是哪一行」，和名称同类。这条原先漂过：同一个概念
   * 在四张表里出现过左 / 右 / 居中三种写法，右对齐还会在窄列里堆出一大片左空白
   * （90px 的列右对齐塞一个 1 位数 = 74px 空白）。
   *
   * 另：列宽贴合内容，别留富余。富余在右对齐列变成左空白、在左对齐列变成右空白。
   */
  align?: "left" | "right" | "center";
  render: (row: T, index: number) => ReactNode;
  /** 列级底色，用来把「系统算的」和「人填的」在视觉上分开 */
  tint?: "readonly" | "editable" | "rival";
  /** 冻结列：给出距左侧的像素偏移。宽表横向滚动时关卡/城市要一直可见 */
  stickyLeft?: number;
  /** 右冻结列：给出距右侧的像素偏移。操作列钉在右边，横向滚动时按钮一直可点（leo 2026-08-27） */
  stickyRight?: number;
}

const TINT = {
  readonly: "bg-fill-light",
  editable: "bg-white",
  rival: "bg-warning-50/40",
} as const;

const ALIGN = { left: "text-left", right: "text-right tabular-nums", center: "text-center" } as const;

/**
 * 列驱动表格。设计稿里 11 处各自手写 <table> + thStyle/tdStyle，
 * 改一次表头背景要改 11 个地方——统一到这里。
 *
 * 刻意不做排序/分页：管理端的表都是「按关卡序号」这种固有顺序，
 * 加了排序反而让策划怀疑自己看到的是不是完整数据。
 */
export function DataTable<T>({
  columns, rows, rowKey, stickyHeader, onRowClick, rowTint, empty, summary, minWidth, fixedLayout, groupStart, virtual, lensRow, rowDrop,
}: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T, index: number) => string | number;
  /**
   * 接口视角：这一行在接口里对应哪一项（关卡号、星数、卡包 id…）。给了，选取模式下点这一行的格子，
   * 面板取的就是这一行的值，而不是第一项做例子。口径见 src/api-lens/blocks.ts 的 row
   */
  lensRow?: (row: T) => string | number;
  stickyHeader?: boolean;
  onRowClick?: (row: T) => void;
  /** 整行底色，用于标出校验未通过的行 */
  rowTint?: (row: T) => string | undefined;
  /**
   * 整行当拖放目标（关卡配置页拖动改编号）。把手由调用方画在某一列里，这里只接住行上的 dragover / drop；
   * className 给拖到这一行上方时的提示线
   */
  rowDrop?: (row: T) => { onDragOver?: (e: DragEvent<HTMLTableRowElement>) => void; onDrop?: (e: DragEvent<HTMLTableRowElement>) => void; className?: string };
  empty?: ReactNode;
  /**
   * 固定列宽。auto 布局下 `width` 只是建议——内容一长就被挤，
   * 实测审计表的时间列被压到 81px、换行成三行。宽表要守住列宽就得用它。
   */
  fixedLayout?: boolean;
  /** 置顶合计行。按列 key 给出内容，缺的列渲染空 */
  summary?: Partial<Record<string, ReactNode>>;
  minWidth?: number;
  /**
   * 这一行是不是一个分组的开头（如「同一栋建筑的 1★」）。
   * 是就在整行上方压一条粗分隔线——N×M 平铺成 25 行以后，
   * 没有分隔线根本看不出哪五行属于同一栋。
   */
  groupStart?: (row: T, i: number) => boolean;
  /**
   * 窗口化：只挂视口附近的行，其余用两条占位行撑高度。上千行、每行好几个输入框的表
   * （萌宠小镇星级表 1000 星 × 3 个输入）整张挂上，每敲一个键要重画 3000 个输入框、卡 200ms。
   * 页面本身是滚动容器（表格外层只横向滚），所以按 window 的滚动位置算。行高从已挂的第一行量。
   */
  virtual?: boolean;
}) {
  const t = useT();
  const wrap = useRef<HTMLDivElement>(null);
  const [win, setWin] = useState<{ start: number; end: number; rowH: number }>({ start: 0, end: rows.length, rowH: ROW_H_FALLBACK });
  const windowed = !!virtual && rows.length > VIRTUAL_MIN_ROWS;
  useEffect(() => {
    if (!windowed) return;
    let raf = 0;
    const measure = () => {
      raf = 0;
      const el = wrap.current;
      if (!el) return;
      const firstRow = el.querySelector<HTMLTableRowElement>("tbody tr[data-row]");
      const rowH = firstRow?.getBoundingClientRect().height || ROW_H_FALLBACK;
      const head = el.querySelector("thead")?.getBoundingClientRect().height ?? 0;
      // 合计行比数据行矮（单行 40px），单独量，不拿数据行高顶
      const sumH = summary ? (el.querySelector<HTMLTableRowElement>("tbody tr:not([data-row]):not([aria-hidden])")?.getBoundingClientRect().height ?? rowH) : 0;
      const top = el.getBoundingClientRect().top + window.scrollY + head + sumH;
      const first = Math.floor((window.scrollY - top) / rowH);
      const count = Math.ceil(window.innerHeight / rowH);
      const start = Math.max(0, first - OVERSCAN);
      const end = Math.min(rows.length, first + count + OVERSCAN);
      setWin((w) => (w.start === start && w.end === end && w.rowH === rowH ? w : { start, end, rowH }));
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(measure); };
    measure();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onScroll); if (raf) cancelAnimationFrame(raf); };
  }, [windowed, rows.length, summary != null]);
  const start = windowed ? Math.min(win.start, rows.length) : 0;
  const end = windowed ? Math.min(win.end, rows.length) : rows.length;
  if (!rows.length) {
    return <div className="rounded-base border border-line-lighter bg-white py-10 text-center text-sm text-ink-placeholder">{empty ?? t("table.empty")}</div>;
  }
  return (
    <div ref={wrap} className="overflow-auto rounded-base border border-line-lighter">
      <table
        style={{ minWidth, tableLayout: fixedLayout ? "fixed" : undefined }}
        className="w-full border-separate border-spacing-0 text-sm"
      >
        {fixedLayout && (
          <colgroup>
            {columns.map((c) => <col key={c.key} style={{ width: c.width }} />)}
          </colgroup>
        )}
        <thead className={stickyHeader ? "sticky top-0 z-10" : undefined}>
          <tr>
            {columns.map((c) => (
              <th
                key={c.key}
                data-api={c.api}
                style={{ width: c.width, ...(c.stickyLeft != null ? { position: "sticky", left: c.stickyLeft, zIndex: 3 } : null), ...(c.stickyRight != null ? { position: "sticky", right: c.stickyRight, zIndex: 3 } : null) }}
                className={`border-b border-line bg-fill-light px-2 py-2 text-xs font-semibold text-ink-regular ${ALIGN[c.align ?? "left"]} ${c.stickyLeft ? "shadow-[2px_0_0_theme(colors.line.lighter)]" : ""} ${c.stickyRight != null ? "shadow-[-2px_0_0_theme(colors.line.lighter)]" : ""}`}
              >
                {c.title}
                {c.sub && <div className="font-normal text-ink-placeholder">{c.sub}</div>}
                {c.api && <div className="font-normal"><ApiTag id={c.api} /></div>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {summary && (
            <tr className="bg-fill-light font-semibold">
              {columns.map((c) => (
                <td
                  key={c.key}
                  style={c.stickyLeft != null ? { position: "sticky", left: c.stickyLeft, zIndex: 2 } : c.stickyRight != null ? { position: "sticky", right: c.stickyRight, zIndex: 2 } : undefined}
                  className={`h-row border-b border-line-lighter bg-fill-light px-2 text-ink-regular ${ALIGN[c.align ?? "left"]} ${c.stickyLeft ? "shadow-[2px_0_0_theme(colors.line.lighter)]" : ""} ${c.stickyRight != null ? "shadow-[-2px_0_0_theme(colors.line.lighter)]" : ""}`}
                >
                  {summary[c.key] ?? null}
                </td>
              ))}
            </tr>
          )}
          {windowed && start > 0 && <tr aria-hidden style={{ height: start * win.rowH }}><td colSpan={columns.length} /></tr>}
          {rows.slice(start, end).map((row, k) => { const i = start + k; return (
            <tr
              key={rowKey(row, i)}
              data-row={i}
              data-lens-row={lensRow ? String(lensRow(row)) : undefined}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              onDragOver={rowDrop?.(row).onDragOver}
              onDrop={rowDrop?.(row).onDrop}
              className={`${rowTint?.(row) ?? "bg-white"} ${onRowClick ? "cursor-pointer hover:bg-primary-50" : ""} ${rowDrop?.(row).className ?? ""}`}
            >
              {columns.map((c) => (
                <td
                  key={c.key}
                  style={c.stickyLeft != null ? { position: "sticky", left: c.stickyLeft, zIndex: 2 } : c.stickyRight != null ? { position: "sticky", right: c.stickyRight, zIndex: 2 } : undefined}
                  className={`h-row overflow-hidden border-b border-line-lighter ${groupStart?.(row, i) ? "border-t-2 border-t-line-light" : ""} px-2 text-ink-regular ${ALIGN[c.align ?? "left"]} ${c.tint ? TINT[c.tint] : ""} ${c.cellClass?.(row) ?? ""} ${c.stickyLeft != null || c.stickyRight != null ? "bg-inherit" : ""} ${c.stickyLeft ? "shadow-[2px_0_0_theme(colors.line.lighter)]" : ""} ${c.stickyRight != null ? "shadow-[-2px_0_0_theme(colors.line.lighter)]" : ""}`}
                >
                  <ControlLabel.Provider value={typeof c.title === "string" ? c.title : undefined}>{c.render(row, i)}</ControlLabel.Provider>
                </td>
              ))}
            </tr>
          ); })}
          {windowed && end < rows.length && <tr aria-hidden style={{ height: (rows.length - end) * win.rowH }}><td colSpan={columns.length} /></tr>}
        </tbody>
      </table>
    </div>
  );
}
